import React, {
  useCallback, useEffect, useRef, useState,
} from "react";
import {
  Modal, Button, Spin, Empty,
} from "antd";
import { toast } from "react-toastify";
import { getMLCountQC, submitMLCountQC } from "../../../../actions/qc";
import useAbortController from "../../../Common/hooks/useAbortController";
import ConfirmationModal from "../../../Common/ConfirmationModal";
import AnnotateKonvaCanvas from "./AnnotateKonvaCanvas";
import { getAnnotationNameStem } from "./utils";
import "./AnnotateImageModal.css";

const AnnotateImageModal = ({
  open,
  onCancel,
  title = "Annotate image",
  annotationPath,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [annotationData, setAnnotationData] = useState(null);
  const [fetchErrorMessage, setFetchErrorMessage] = useState(null);
  const [viewMode, setViewMode] = useState("qc"); // "qc" | "original"
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
  const annotateCanvasRef = useRef(null);
  const { abortController, setNewAbortController, clearAbortController } = useAbortController();

  useEffect(() => {
    if (!annotationPath) {
      return undefined;
    }

    const fetchAnnotationData = async () => {
      setFetchErrorMessage(null);
      setLoading(true);
      const newAbortController = setNewAbortController();

      const annotationId = getAnnotationNameStem(annotationPath);
      const response = await getMLCountQC(annotationId, newAbortController.signal);

      if (response.success) {
        const result = response.result;
        const roiPath = result?.roi_path;

        if (!roiPath) {
          toast.error("Missing ROI image path");
          setOriginalData(null);
          setAnnotationData(null);
          setFetchErrorMessage("Missing ROI image path");
        } else {
          const originalPayload = {
            ...result,
            colony_box_list: result?.colony_box_list || [],
            labels: result?.labels || [],
          };

          const updated = result?.updated_data;
          const qcPayload = updated
            ? {
              ...result,
              colony_box_list: updated?.colony_box_list || originalPayload.colony_box_list,
              labels: updated?.labels || originalPayload.labels,
              updated_data: updated,
            }
            : originalPayload;

          setOriginalData(originalPayload);
          setAnnotationData(qcPayload);
          setFetchErrorMessage(null);
        }
      } else if (!response.canceled) {
        toast.error(response.message || "Failed to fetch annotation data.");
        setOriginalData(null);
        setAnnotationData(null);
        setFetchErrorMessage(response.message || "Failed to load annotation data");
      }

      setLoading(false);
      clearAbortController();
    };

    fetchAnnotationData();
    return () => {
      if (abortController.current) {
        abortController.current.abort(); // eslint-disable-line
      }
    };
  }, [annotationPath]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (payload) => {
    setSubmitLoading(true);
    try {
      const { id, colony_box_list, labels } = payload;
      const updatedData = { colony_box_list, labels };
      const response = await submitMLCountQC(id, updatedData);
      if (response.success) {
        toast.success(response.message || "Successfully updated");
        setConfirmSubmitOpen(false);
        onCancel();
      } else if (!response.canceled) {
        toast.error(response.message);
      }
    } finally {
      setSubmitLoading(false);
      setConfirmSubmitOpen(false);
    }
  };

  const isEditsChanged = (current, next) => {
    const a = current || {};
    const b = next || {};
    return JSON.stringify(a.colony_box_list || []) !== JSON.stringify(b.colony_box_list || [])
      || JSON.stringify(a.labels || []) !== JSON.stringify(b.labels || []);
  };

  const buildValidatedPayload = useCallback(() => {
    const edits = annotateCanvasRef.current?.getColonyEdits?.();
    if (!edits) return [null, false];

    const nextPayload = {
      id: annotationData._id,
      colony_box_list: edits.colony_box_list,
      labels: edits.labels,
    };

    // if updated_data is not present -> first time edit
    if (!annotationData.updated_data) {
      return [nextPayload, true];
    }

    return [nextPayload, isEditsChanged(annotationData, nextPayload)];
  }, [annotateCanvasRef, annotationData]);

  const handleSubmitClick = () => {
    const [nextPayload, hasChanges] = buildValidatedPayload();
    if (!hasChanges) {
      toast.info("No changes to submit.");
      setConfirmSubmitOpen(false); // close confirmation modal
      return;
    }
    onSubmit(nextPayload);
  };

  const isOriginalView = viewMode === "original";
  const toggleDisabled = loading || isImageLoading || Boolean(fetchErrorMessage);

  let content = (
    <AnnotateKonvaCanvas
      ref={annotateCanvasRef}
      annotationData={annotationData}
      originalAnnotationData={originalData}
      interactionMode={viewMode}
      onImageLoadingChange={setIsImageLoading}
      onImageLoadError={setFetchErrorMessage}
      annotationPath={annotationPath}
    />
  );

  if (loading) {
    content = (
      <div className="AnnotateImageModal__LoadingState">
        <Spin size="large" />
      </div>
    );
  } else if (fetchErrorMessage) {
    content = (
      <div className="AnnotateImageModal__EmptyState">
        <Empty description={fetchErrorMessage} />
      </div>
    );
  }

  return (
    <Modal
      centered
      open={open}
      onCancel={onCancel}
      title={title}
      width="min(95vw, 1400px)"
      footer={null}
      rootClassName="AnnotateImageModal"
      destroyOnClose
      closable={false}
      maskClosable={false}
    >
      <div className="AnnotateImageModal__BodyContent">
        <div className="AnnotateImageModal__ContentArea">{content}</div>
        <div className="AnnotateImageModal__Footer">
          <div className="AnnotateImageModal__FooterLeft">
            <div className="AnnotateImageModal__ViewModeToggle" role="tablist" aria-label="Annotation view mode">
              <button
                type="button"
                className={`AnnotateImageModal__ViewModeOption ${isOriginalView ? "AnnotateImageModal__ViewModeOption--active" : ""}`}
                disabled={toggleDisabled}
                onClick={() => setViewMode("original")}
              >
                Original
              </button>
              <button
                type="button"
                className={`AnnotateImageModal__ViewModeOption ${!isOriginalView ? "AnnotateImageModal__ViewModeOption--active" : ""}`}
                disabled={toggleDisabled}
                onClick={() => setViewMode("qc")}
              >
                QC
              </button>
            </div>
          </div>

          <div className="AnnotateImageModal__FooterRight">
            <Button className="AnnotateImageModal__CancelBtn" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              className="AnnotateImageModal__SubmitBtn"
              type="primary"
              loading={submitLoading}
              disabled={submitLoading || isImageLoading || Boolean(fetchErrorMessage) || isOriginalView || loading}
              onClick={() => {
                setConfirmSubmitOpen(true);
              }}
            >
              Submit
            </Button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        title="Confirm submit"
        bodyText="Are you sure you want to submit these annotation changes?"
        open={confirmSubmitOpen}
        okText="Yes"
        cancelText="No"
        loading={submitLoading}
        onOk={handleSubmitClick}
        onCancel={() => setConfirmSubmitOpen(false)}
      />
    </Modal>
  );
};

export default AnnotateImageModal;
